import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Paperclip, Send, Sparkles } from "lucide-react";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";

/** Garantiefall melden: fertige Reklamations-E-Mail an den Händler (Vorschau, bearbeiten, senden mit Kaufbeleg im Anhang). */
export const WarrantyClaimDialog = ({ warranty, onClose, onSent }) => {
  const [description, setDescription] = useState("");
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const loadDraft = async (desc) => {
    if (!warranty) return;
    setLoading(true);
    try {
      const r = await apiClient.get(`/warranties/${warranty.id}/claim-draft`, { params: { description: desc || "" } });
      setDraft((p) => ({ ...r.data, to: p?.to || r.data.to || "" }));
    } catch (e) { toast.error(e.response?.data?.detail || "Entwurf konnte nicht erstellt werden"); }
    finally { setLoading(false); }
  };

  const warrantyId = warranty?.id;
  useEffect(() => {
    setDescription("");
    setDraft(null);
    if (!warrantyId) return;
    setLoading(true);
    apiClient.get(`/warranties/${warrantyId}/claim-draft`)
      .then((r) => setDraft({ ...r.data, to: r.data.to || "" }))
      .catch((e) => toast.error(e.response?.data?.detail || "Entwurf konnte nicht erstellt werden"))
      .finally(() => setLoading(false));
  }, [warrantyId]);

  const send = async () => {
    if (!draft?.to) { toast.error("Bitte die E-Mail-Adresse des Händlers eingeben."); return; }
    setSending(true);
    try {
      const r = await apiClient.post(`/warranties/${warranty.id}/claim`, { to: draft.to, subject: draft.subject, body: draft.body, description });
      toast.success(`Garantiefall an ${draft.to} gesendet`);
      onSent?.(r.data);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Senden fehlgeschlagen – ist der E-Mail-Versand (SMTP) der Firma eingerichtet?"); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={!!warranty} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="warranty-claim-dialog">
        <DialogHeader><DialogTitle>Garantiefall melden – {warranty?.product_name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Fehlerbeschreibung</Label>
            <div className="flex gap-2">
              <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="z.B. Gerät schaltet sich nach 5 Minuten ab" data-testid="warranty-claim-description" />
              <Button type="button" variant="outline" onClick={() => loadDraft(description)} disabled={loading} title="E-Mail-Text mit dieser Fehlerbeschreibung neu erzeugen" data-testid="warranty-claim-regenerate">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>An (Händler)</Label><Input type="email" value={draft?.to || ""} onChange={(e) => setDraft({ ...draft, to: e.target.value })} placeholder="service@haendler.ch" data-testid="warranty-claim-to" /></div>
            <div><Label>Betreff</Label><Input value={draft?.subject || ""} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} data-testid="warranty-claim-subject" /></div>
          </div>
          <div>
            <Label>E-Mail-Text</Label>
            {loading && !draft ? <div className="py-6 text-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
              : <Textarea rows={14} value={draft?.body || ""} onChange={(e) => setDraft({ ...draft, body: e.target.value })} className="font-mono text-xs" data-testid="warranty-claim-body" />}
          </div>
          <p className="text-xs text-slate-500 inline-flex items-center gap-1" data-testid="warranty-claim-attachment">
            <Paperclip className="h-3 w-3" />
            {draft?.has_attachment ? `Anhang: ${draft.attachment_name || "Kaufbeleg"}` : "Kein Kaufbeleg hinterlegt – E-Mail geht ohne Anhang raus."}
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={send} disabled={sending || loading || !draft} className="bg-emerald-600 hover:bg-emerald-700" data-testid="warranty-claim-send">
            {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />} Senden
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
